import { awaitingRedraft } from '@/lib/agentItems'
import type { AgentAnswerChannel, AgentClaimStage, AgentItem } from '@/types/agent'

export const STAGE_LABELS: Record<AgentClaimStage, string> = {
  triaging: 'triaging',
  investigating: 'investigating',
  drafting: 'drafting a reply',
}

const CHANNEL_LABELS: Record<AgentAnswerChannel, string> = {
  helpdesk: 'the help desk',
  dashboard: 'Agent Relay',
  slack: 'Slack',
  email: 'email',
}

// The help desk's own answers need no "where"; another channel's are named, so staff know why a card closed.
const onChannel = (channel: AgentAnswerChannel) => (channel === 'helpdesk' ? '' : ` on ${CHANNEL_LABELS[channel]}`)

export const DENY_REASONS = ['Wrong information', 'Wrong account or site', 'Tone is off', 'Not ready to reply yet', 'Needs a person to handle it']

export type CardTone = 'pending' | 'waiting' | 'done' | 'denied' | 'closed'

export interface ItemState {
  tone: CardTone
  label: string
  /** The answer's words worth showing under the label: guidance, the deny reason, the edited reply, an answer */
  detail?: string
}

/**
 * What a draft or question card says about itself. A draft answered with guidance waits for the agent until it posts
 * something newer; anything answered elsewhere says where.
 */
export function itemState(item: AgentItem, all: AgentItem[]): ItemState {
  if (item.status === 'pending') return { tone: 'pending', label: item.kind === 'draft' ? 'Waiting for someone to send it' : 'Waiting for an answer' }
  if (item.status === 'superseded') return { tone: 'closed', label: 'Superseded by a newer reply' }
  if (item.status === 'expired') return { tone: 'closed', label: 'Expired' }

  const answer = item.answer
  if (!answer) return { tone: 'done', label: 'Answered' }
  const by = `${answer.by.name}${onChannel(answer.channel)}`
  const detail = answer.text ?? undefined
  switch (answer.optionId) {
    case 'approve':
      // Only a send from the ticket posts a reply now; an approval from another channel is an older card's.
      if (answer.channel === 'helpdesk') return { tone: 'done', label: answer.text ? `Edited and sent by ${by}` : `Sent by ${by}` }
      return { tone: 'done', label: answer.text ? `Edited and approved by ${by}` : `Approved by ${by}` }
    case 'decline':
      return { tone: 'denied', label: `Denied by ${by}`, detail }
    case 'guidance':
      return awaitingRedraft(item, all)
        ? { tone: 'waiting', label: `Waiting for the agent: ${by} replied`, detail }
        : { tone: 'closed', label: `${by} replied to the agent`, detail }
    case 'stand_down':
      return { tone: 'closed', label: `Ignored by ${by}: the agent stood down` }
    default:
      return { tone: 'done', label: `Answered by ${by}`, detail }
  }
}

export const stoodDownLabel = (byName: string, channel: AgentAnswerChannel) => `Agent stood down by ${byName}${onChannel(channel)}`
